// Every door in checks here before registering a file: is its name already taken
// where it is going (H28)? Taken means a file there — one profile's, when a
// profile is given — or a file of that name waiting in the table's unallocated
// queue, uploaded but not yet approved, which has reserved the name too. Names
// are compared through their equivalent forms (.xls ≡ .xlsx ≡ .xlsm, .doc ≡ .docx),
// as the backend canonicalises them on ingest.
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { nameVariants } from '../utils/uploadNames';

const IN_QUERY_CAP = 30;   // Firestore's cap on an `in` filter

const chunked = (items, size) => {
  const groups = [];
  for (let i = 0; i < items.length; i += size) groups.push(items.slice(i, i + size));
  return groups;
};

// The names among `names` that one table already holds.
export async function takenNamesInTable({ companyId, tableId, names, profileId = null }) {
  const files = collection(db, `users/${companyId}/tables/${tableId}/files`);
  const staging = collection(db, `users/${companyId}/tables/${tableId}/unallocated_files`);
  const taken = new Set();
  const queries = chunked(Array.from(new Set(names)), IN_QUERY_CAP).flatMap((chunk) => [
    profileId
      ? query(files, where('name', 'in', chunk), where('data_profile_id', '==', profileId))
      : query(files, where('name', 'in', chunk)),
    query(staging, where('name', 'in', chunk)),
  ]);
  await Promise.all(queries.map(async (q) => {
    (await getDocs(q)).docs.forEach((d) => taken.add(d.data().name));
  }));
  return taken;
}

// The chosen files a table already holds under any equivalent name.
export async function findExistingFiles({ companyId, tableId, files, profileId = null }) {
  const variantsOf = new Map(files.map((file) => [file, nameVariants(file.name)]));
  const taken = await takenNamesInTable({
    companyId, tableId, profileId, names: Array.from(variantsOf.values()).flat(),
  });
  return files.filter((file) => variantsOf.get(file).some((name) => taken.has(name)));
}

// For the workspace door, whose destination is not chosen yet: the chosen files
// any of the given tables already holds, each with the names of the tables that do.
export async function findExistingFilesAcrossTables({ companyId, tables, files }) {
  const perTable = await Promise.all(tables.map(async (table) => ({
    table,
    found: await findExistingFiles({ companyId, tableId: table.id, files }),
  })));
  const holders = new Map();
  perTable.forEach(({ table, found }) => found.forEach((file) => {
    holders.set(file, [...(holders.get(file) || []), table.name || table.id]);
  }));
  return files.filter((file) => holders.has(file)).map((file) => ({ file, tables: holders.get(file) }));
}
